import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useLanguage } from '../context/LanguageContext';
import { ExternalLink, Github, ChevronDown, ChevronUp, Info, X } from 'lucide-react';

interface Project {
  id: number;
  name: string;
  category: 'websites' | 'apps' | 'scripts';
  image: string;
  images?: string[];
  tags: string[];
  url: string;
  githubUrl?: string;
  description: string;
}

const ProjectModal = ({ project, onClose, onImageClick }: { project: Project; onClose: () => void; onImageClick: (url: string) => void }) => {
  const { t } = useLanguage();
  
  const isUrlEmpty = !project.url || project.url === '';

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-6 bg-bg-dark/90 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.9, opacity: 0, y: 20 }}
        className="glass w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-3xl p-6 md:p-10 relative no-scrollbar"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-6 right-6 p-2 glass hover:bg-white/10 rounded-full transition-all z-10"
        >
          <X className="w-6 h-6" />
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          <div className="space-y-6">
            <h3 className="text-3xl md:text-4xl font-display font-black gradient-text">
              {project.name}
            </h3>
            
            <div className="space-y-4">
              <h4 className="text-sm font-mono uppercase tracking-widest text-white/40">
                {t('portfolio.tools_used')}
              </h4>
              <div className="flex flex-wrap gap-2">
                {project.tags.map((tag, idx) => (
                  <span key={`${tag}-${idx}`} className="px-4 py-2 bg-white/5 rounded-xl text-xs font-bold text-primary border border-white/10">
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            <div className="space-y-4">
              <p className="text-white/70 leading-relaxed text-lg">
                {project.description}
              </p>
            </div>

            <div className="flex flex-wrap gap-4 pt-6">
              {isUrlEmpty ? (
                <button
                  disabled
                  className="flex-1 min-w-[200px] py-4 bg-white/5 text-white/20 font-black rounded-2xl flex items-center justify-center gap-3 cursor-not-allowed border border-white/10"
                >
                  {t('portfolio.view_project')}
                  <ExternalLink className="w-5 h-5 opacity-20" />
                </button>
              ) : (
                <a
                  href={project.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 min-w-[200px] py-4 bg-primary text-bg-dark font-black rounded-2xl flex items-center justify-center gap-3 hover:scale-[1.02] active:scale-[0.98] transition-all group"
                >
                  {t('portfolio.view_project')}
                  <ExternalLink className="w-5 h-5 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                </a>
              )}
              {project.githubUrl && (
                <a
                  href={project.githubUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-16 h-16 glass rounded-2xl flex items-center justify-center hover:bg-white/10 hover:border-primary/50 transition-all group"
                >
                  <Github className="w-6 h-6 text-white/60 group-hover:text-primary group-hover:scale-110 transition-all" />
                </a>
              )}
            </div>
          </div>

          <div className="space-y-6">
            <div 
              className="rounded-2xl overflow-hidden border border-white/10 aspect-video cursor-zoom-in group"
              onClick={() => onImageClick(project.image)}
            >
              <img
                src={project.image}
                alt={project.name}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                referrerPolicy="no-referrer"
              />
            </div>
            {project.images && project.images.length > 0 && (
              <div className="grid grid-cols-2 gap-4">
                {project.images.map((img, idx) => (
                  <div 
                    key={idx} 
                    className="rounded-xl overflow-hidden border border-white/10 aspect-video cursor-zoom-in group"
                    onClick={() => onImageClick(img)}
                  >
                    <img
                      src={img}
                      alt={`${project.name} ${idx + 1}`}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      referrerPolicy="no-referrer"
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

const Lightbox = ({ url, onClose }: { url: string; onClose: () => void }) => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/95 backdrop-blur-md"
      onClick={onClose}
    >
      <button
        onClick={onClose}
        className="absolute top-6 right-6 p-3 bg-white/10 hover:bg-white/20 rounded-full transition-all z-10"
      >
        <X className="w-8 h-8 text-white" />
      </button>
      <motion.img
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.8, opacity: 0 }}
        src={url}
        alt="Lightbox"
        className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        referrerPolicy="no-referrer"
      />
    </motion.div>
  );
};

const Portfolio = () => {
  const { t } = useLanguage();
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({
    websites: false,
    apps: false,
    scripts: false,
  });
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [lightboxImage, setLightboxImage] = useState<string | null>(null);

  const projects: Project[] = [
    // Websites
    { 
      id: 1, 
      name: 'Silvia POS Web Site', 
      category: 'websites', 
      image: '/assets/portfolio/silvia01.png', 
      tags: ['WordPress', 'PHP', 'MySQL', 'CSS', 'SEO'],
      url: '',
      description: 'Website for the SilviaPoS billing system built with WordPress, featuring custom PHP/CSS development, pricing tables, requirements, product information, contact section, and support chat.'
    },
    { 
      id: 2, 
      name: 'OA Shop E-commerce', 
      category: 'websites', 
      image: '/assets/portfolio/oa01.png', 
      tags: ['Wordpress', 'MySQL', 'Woocommerce', 'PHP', 'JavaScript', 'CSS'],
      url: '',
      description: 'Modern e-commerce experience built on Wordpress with Woocommerce plugin, focusing on conversion rates and mobile-first responsive design.'
    },
    { 
      id: 3, 
      name: 'BlueBronx Web Site', 
      category: 'websites', 
      image: '/assets/portfolio/bluebronx01.png', 
      tags: ['Wordpress', 'MySQL', 'CSS', 'SEO'],
      url: 'https://bluebronx.com/',
      description: 'Official website for BlueBronx, a major men´s jeans manufacturer and retailer in the Dominican Republic, built with WordPress, custom CSS, SEO, and product catalog.'
    },
    { 
      id: 4, 
      name: 'Distribuidora Fischer Web Site', 
      category: 'websites', 
      image: '/assets/portfolio/distribuidorafischer01.png', 
      tags: [ 'Wordpress', 'Woocomerce', 'PHP', 'HTML', 'CSS', 'JS'], 
      url: 'https://distribuidorafischer.com.do/', 
      description: 'Corporate website for Distribuidora Fischer developed in WordPress, featuring an online product catalog powered by WooCommerce for plastics, disposables, and office supplies.' },
    { 
      id: 5, 
      name: 'Cemedi Web Site', 
      category: 'websites', 
      image: '/assets/portfolio/cemedi01.png', 
      tags: ['Wordpress', 'MySQL', 'CSS', 'SEO'], 
      url: 'https://cemedi.com.do/', 
      description: 'Website for CEMEDI, a major medical imaging and diagnostics center in Santo Domingo, built with WordPress, featuring appointment forms with medical indications and WhatsApp chat.' },
    { 
      id: 6, 
      name: 'Logistica Legal Web Site', 
      category: 'websites', 
      image: '/assets/portfolio/logistica01.png', 
      tags: ['Wordpress', 'MySQL', 'CSS', 'SEO'], 
      url: 'https://logisticalegal.com.do/', 
      description: 'Website for Logística Legal, a law firm in Santo Domingo, built with WordPress, featuring a bilingual interface in Spanish and English.' },
    
    // Apps
    { 
      id: 7, 
      name: 'Lordb - Inventory App', 
      category: 'apps', 
      image: '/assets/portfolio/lordb01.png',
      images: ['/assets/portfolio/lordb02.png', '/assets/portfolio/lordb03.png'], 
      tags: ['PHP', 'HTML', 'CSS', 'JS', 'Bootstrap', 'MySQL', 'AJAX'],
      url: 'https://mediaexperto.com/temp/cris05/projects/lordb/',
      githubUrl: 'https://github.com/nvidiati/lordb',
      description: 'LordB is a professional web application for efficient inventory management, helping small and medium businesses replace spreadsheets with a centralized system.'
    },
    { 
      id: 8, 
      name: 'Lottery Numbers', 
      category: 'apps', 
      image: '/assets/portfolio/numeros01.png', 
      images: ['/assets/portfolio/numeros02.png', '/assets/portfolio/numeros03.png'], 
      tags: ['PHP', 'JavaScript', 'HTML', 'CSS', 'Firebase'],
      url: 'https://mediaexperto.com/temp/cris05/projects/numeros/',
      githubUrl: 'https://github.com/nvidiati/numeros',
      description: 'Web application designed for lottery shops to display winning numbers on full-screen monitors, showing results from the last two days and weekly summaries.'
    },
    { 
      id: 9, 
      name: 'SisSalon - Beauty Salons App', 
      category: 'apps', 
      image: '/assets/portfolio/sissalon01.png',
      images: ['/assets/portfolio/sissalon02.png'],
      tags: ['PHP', 'Laravel', 'Blade', 'Bootstrap', 'JavaScript', 'MySQL', 'Composer'],
      url: '',
      githubUrl: 'https://github.com/nvidiati/sissalon',
      description: 'SisSalon is a Laravel-based management system for beauty salons, centralizing appointments, inventory control, financial management, and daily business operations.'
    },
    { 
      id: 10, 
      name: 'UPC-Identify App', 
      category: 'apps', 
      image: '/assets/portfolio/upc01.png', 
      tags: ['PHP', 'HTML', 'CSS', 'API REST', 'HTTP Requests', 'UPCitemDB API'], 
      url: 'https://mediaexperto.com/temp/cris05/projects/UPC-identify/', 
      githubUrl: 'https://github.com/nvidiati/UPC-Identify', 
      description: 'PHP web application that identifies electronic products using UPC barcodes by querying the UPCitemDB API to retrieve product details and price ranges.' },
    
    // Scripts
    { 
      id: 11, 
      name: 'NominaBot - Node Script', 
      category: 'scripts', 
      image: '/assets/portfolio/nomina01.png', 
      images: ['/assets/portfolio/nomina02.png', '/assets/portfolio/nomina03.png'], 
      tags: ['Node.js', 'JS', 'Bash'],
      url: '',
      githubUrl: 'https://github.com/nvidiati/NominaBot',
      description: 'NominaBot is a Node.js automation tool that sends employee payroll slips through WhatsApp Web, processing generated payslips and delivering them automatically.'
    },
    { 
      id: 12, 
      name: 'LcBarcode - PHP Script', 
      category: 'scripts', 
      image: '/assets/portfolio/bc01.png', 
      images: ['/assets/portfolio/bc2.jpg', '/assets/portfolio/bc3.jpg'], 
      tags: ['PHP', 'HTML', 'CSS', 'html2pdf', 'Barcode generation'],
      url: 'https://mediaexperto.com/temp/cris05/projects/lcbarcode/',
      githubUrl: 'https://github.com/nvidiati/lcbarcode',
      description: 'PHP web application using html2pdf to generate printable product labels with barcodes, pricing, and product details, dynamically generated via GET parameters.'
    },
    { 
      id: 13, 
      name: 'Catering Order Quotations - WP Plugin', 
      category: 'scripts', 
      image: '/assets/portfolio/cotizador01.png', 
      images: ['/assets/portfolio/cotizador02.png', '/assets/portfolio/cotizador03.png'], 
      tags: ['PHP', 'JS', 'CSS'],
      url: '',
      githubUrl: 'https://github.com/nvidiati/picaderas-cotizador-plugin',
      description: 'WordPress plugin for catering order quotations, featuring automatic price calculation, delivery fees, and direct order submission to WhatsApp.'
    },
    { 
      id: 14, 
      name: 'Form-to-Table - WP Plugin', 
      category: 'scripts', 
      image: '/assets/portfolio/publica01.png', 
      images: ['/assets/portfolio/publica02.png', '/assets/portfolio/publica03.png'], 
      tags: ['PHP', 'CSS', 'JS'], 
      url: '', 
      githubUrl: 'https://github.com/nvidiati/plugin-ufhec-publica', 
      description: 'Robust WordPress plugin for managing external user submissions, storing entries for review and dynamically displaying approved data in a frontend table.' },
  ];

  const toggleCategory = (category: string) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const renderCategory = (categoryId: 'websites' | 'apps' | 'scripts', title: string) => {
    const categoryProjects = projects.filter(p => p.category === categoryId);
    const isExpanded = expandedCategories[categoryId];
    const visibleProjects = isExpanded ? categoryProjects : categoryProjects.slice(0, 3);
    const hasMore = categoryProjects.length > 3;

    return (
      <div key={categoryId} className="mb-20 last:mb-0">
        <div className="flex items-center gap-4 mb-10">
          <h3 className="text-2xl md:text-3xl font-display font-bold text-white/90">
            {title}
          </h3>
          <div className="flex-1 h-[1px] bg-white/10" />
          <span className="text-xs font-mono text-white/30 uppercase tracking-widest">
            {categoryProjects.length} Projects
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <AnimatePresence mode="popLayout">
            {visibleProjects.map((project, idx) => {
              const isUrlEmpty = !project.url || project.url === '';
              
              return (
                <motion.div
                  key={project.id}
                  layout
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.4, delay: isExpanded ? 0 : idx * 0.1 }}
                  className="group relative glass rounded-3xl overflow-hidden hover:border-primary/50 transition-all"
                >
                  {/* Image */}
                  <div 
                    className="aspect-video overflow-hidden cursor-zoom-in"
                    onClick={() => setLightboxImage(project.image)}
                  >
                    <img
                      src={project.image}
                      alt={project.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                      referrerPolicy="no-referrer"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-bg-dark via-transparent to-transparent opacity-60" />
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <h4 className="text-xl font-display font-bold mb-4">{project.name}</h4>
                    
                    <div className="flex flex-wrap gap-2 mb-6">
                      {project.tags.map((tag, idx) => (
                        <span key={`${tag}-${idx}`} className="px-3 py-1 bg-white/5 rounded-full text-[10px] font-bold text-white/50 border border-white/10">
                          {tag}
                        </span>
                      ))}
                    </div>

                    <div className="flex items-center gap-3 relative z-10">
                      {isUrlEmpty ? (
                        <button
                          disabled
                          className="flex-1 py-3 bg-white/5 text-white/20 font-bold rounded-xl flex items-center justify-center gap-2 cursor-not-allowed border border-white/10"
                        >
                          {t('portfolio.view_project')}
                          <ExternalLink className="w-4 h-4 opacity-20" />
                        </button>
                      ) : (
                        <a
                          href={project.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex-1 py-3 bg-primary/10 hover:bg-primary/20 text-primary font-bold rounded-xl transition-all flex items-center justify-center gap-2 group/btn cursor-pointer"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {t('portfolio.view_project')}
                          <ExternalLink className="w-4 h-4 group-hover/btn:translate-x-1 group-hover/btn:-translate-y-1 transition-transform" />
                        </a>
                      )}
                    
                    {categoryId !== 'websites' && project.githubUrl && (
                      <a
                        href={project.githubUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-12 h-12 glass hover:bg-white/10 rounded-xl flex items-center justify-center transition-all group/gh cursor-pointer"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Github className="w-5 h-5 text-white/60 group-hover/gh:text-primary transition-colors" />
                      </a>
                    )}

                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedProject(project);
                      }}
                      className="w-12 h-12 glass hover:bg-white/10 rounded-xl flex items-center justify-center transition-all group/info"
                    >
                      <Info className="w-5 h-5 text-white/60 group-hover/info:text-primary transition-colors" />
                    </button>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>

        {hasMore && (
          <motion.div 
            layout
            className="mt-12 flex justify-center"
          >
            <button
              onClick={() => toggleCategory(categoryId)}
              className="flex items-center gap-2 px-8 py-3 glass rounded-full text-sm font-bold hover:border-primary/50 hover:text-primary transition-all group"
            >
              {isExpanded ? (
                <>
                  {t('portfolio.show_less')}
                  <ChevronUp className="w-4 h-4 group-hover:-translate-y-1 transition-transform" />
                </>
              ) : (
                <>
                  {t('portfolio.show_more')}
                  <ChevronDown className="w-4 h-4 group-hover:translate-y-1 transition-transform" />
                </>
              )}
            </button>
          </motion.div>
        )}
      </div>
    );
  };

  return (
    <section id="portfolio" className="py-24 relative overflow-hidden bg-white/5">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-20"
        >
          <h2 className="text-4xl md:text-5xl font-display font-black mb-4 tracking-tight">
            {t('portfolio.title')}
          </h2>
          <div className="w-20 h-1 bg-accent mx-auto rounded-full" />
        </motion.div>

        <div className="space-y-24">
          {renderCategory('websites', t('portfolio.categories.websites'))}
          {renderCategory('apps', t('portfolio.categories.apps'))}
          {renderCategory('scripts', t('portfolio.categories.scripts'))}
        </div>
      </div>

      <AnimatePresence>
        {selectedProject && (
          <ProjectModal
            project={selectedProject}
            onClose={() => setSelectedProject(null)}
            onImageClick={(url) => setLightboxImage(url)}
          />
        )}
        {lightboxImage && (
          <Lightbox
            url={lightboxImage}
            onClose={() => setLightboxImage(null)}
          />
        )}
      </AnimatePresence>
    </section>
  );
};

export default Portfolio;
